// @section 1 AccountWorkspace — unified account-scoped workspace shell
//
// One page per account, persistent header + lens switcher. The Account graph
// is hq_crm.companies (root) → deals, contacts, activities, engagements,
// BD facts/asks/findings/offers, tasks. The lens switcher pivots the body
// without re-fetching the header.
//
// Seven lenses (flattened from the earlier 4-tab arrangement on 2026-05-27):
//   Overview   — at-a-glance + the full CRM surface (contacts, deals, activity)
//   Facts      — captured facts (was a BD sub-tab)
//   Asks       — open asks + the Ask → "+ Add to offer" composer (was a BD sub-tab)
//   Findings   — research items (was a BD sub-tab)
//   Workplan   — BD-derived operator pipeline + literal hq_pm.tasks
//   Next Step  — single-most-important next action picker
//   Delivery   — offers + engagements → workstreams → deliverables
//
// The old Relationship lens was absorbed into Overview; the old BD lens was
// flattened so its three sub-tabs become top-level tabs.

import { useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { getAccount, type AccountAggregate } from '../api';
import { useIsMobile } from '../lib/useIsMobile';
import AccountOverviewLens from './account/AccountOverviewLens';
import AccountFactsLens from './account/AccountFactsLens';
import AccountAsksLens from './account/AccountAsksLens';
import AccountFindingsLens from './account/AccountFindingsLens';
import AccountWorkplanLens from './account/AccountWorkplanLens';
import AccountNextStepLens from './account/AccountNextStepLens';
import AccountDeliveryLens from './account/AccountDeliveryLens';
// Financials extracted to its own top-level module (sidebar item under
// "Financials" → /erp) — it's a cross-account view, not a per-account lens.

type LensKey = 'overview' | 'facts' | 'asks' | 'findings' | 'pm' | 'next-step' | 'delivery';

type LensDef = {
  key: LensKey;
  label: string;
};

const LENSES: LensDef[] = [
  { key: 'overview',  label: 'Overview' },
  { key: 'facts',     label: 'Facts' },
  { key: 'asks',      label: 'Asks' },
  { key: 'findings',  label: 'Findings' },
  { key: 'pm',        label: 'Workplan' },
  { key: 'next-step', label: 'Next Step' },
  { key: 'delivery',  label: 'Delivery' },
];

export default function AccountWorkspace() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<AccountAggregate | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // @block 1.0 Phone: the shell owns the scroll for every lens; drop the desktop
  // negative-margin bleed and stack the title/KPI header.
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!id) return;
    setData(null);
    setErr(null);
    getAccount(id)
      .then(setData)
      .catch((e) => setErr(String(e)));
  }, [id]);

  if (!id) return <Navigate to="/accounts" replace />;

  if (err) {
    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <NavLink to="/accounts">← Accounts</NavLink>
        </div>
        <h1>Account</h1>
        <div className="placeholder" style={{ color: '#e87e7e' }}>{err}</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <NavLink to="/accounts">← Accounts</NavLink>
        </div>
        <h1>Loading account…</h1>
      </div>
    );
  }

  const { company } = data;

  // Last activity timestamp — the most recent touch on the account (pulled
  // from the aggregator's recentActivities list, which is already sorted desc).
  const lastActivity = data.recentActivities[0]?.occurredAt;

  return (
    // Pull the workspace flush to the content gutter so the title strip and
    // stat rail fill the available width — the old layout left the top-right
    // empty while the stats wrapped underneath the title.
    <div style={isMobile
      ? { height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '12px 14px 24px' }
      : { margin: '-12px -8px 0' }}>
      {/* @block 1.1 Back-link */}
      <div style={{ marginBottom: 10 }}>
        <NavLink to="/accounts" style={{ fontSize: 13, color: '#6b7a90' }}>← Accounts</NavLink>
      </div>

      {/* @block 1.2 Persistent account header — two-column layout.
          Left: title + domain + industry stack.
          Right: compact KPI rail using the dead space the old layout left blank. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) auto',
        gap: isMobile ? 12 : 24,
        alignItems: 'start',
        marginBottom: 14,
      }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: 28, lineHeight: 1.1 }}>{company.name}</h1>
          <div style={{ marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'baseline', fontSize: 13 }}>
            {company.domain && (
              <a href={`https://${company.domain}`} target="_blank" rel="noreferrer"
                 style={{ color: '#7eb6e8' }}>
                {company.domain} ↗
              </a>
            )}
            {company.industry && <span style={{ color: '#6b7a90' }}>{company.industry}</span>}
            {lastActivity && (
              <span style={{ color: '#6b7a90' }}>
                last touch {new Date(lastActivity).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
        <HeaderStats data={data} />
      </div>

      {/* @block 1.3 Lens switcher */}
      <div className="tabs" style={{ marginTop: 0 }}>
        {LENSES.map((l) => (
          <NavLink
            key={l.key}
            to={`/accounts/${id}/${l.key}`}
            className={({ isActive }) => (isActive ? 'tab active' : 'tab')}
          >
            {l.label}
          </NavLink>
        ))}
      </div>

      {/* @block 1.4 Lens body */}
      <div style={{ marginTop: 16 }}>
        <Routes>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview"  element={<AccountOverviewLens data={data} />} />
          <Route path="facts"     element={<AccountFactsLens data={data} />} />
          <Route path="asks"      element={<AccountAsksLens data={data} />} />
          <Route path="findings"  element={<AccountFindingsLens data={data} />} />
          <Route path="pm"        element={<AccountWorkplanLens data={data} />} />
          <Route path="next-step" element={<AccountNextStepLens data={data} />} />
          <Route path="delivery"  element={<AccountDeliveryLens data={data} />} />
          {/* Back-compat redirects for retired lens paths. /bd was flattened
              into Facts/Asks/Findings (defaults to Facts); /crm folded into
              Overview; /financials is its own top-level module at /erp. */}
          <Route path="bd"         element={<Navigate to="../facts" replace />} />
          <Route path="crm"        element={<Navigate to="../overview" replace />} />
          <Route path="financials" element={<Navigate to="../overview" replace />} />
          {/* Any unknown sub-path → Overview */}
          <Route path="*" element={<Navigate to="overview" replace />} />
        </Routes>
      </div>
    </div>
  );
}

// @section 2 Header stats — compact KPI rail filling the dead space top-right
// of the title block. Six stats laid out as a 3×2 mini-grid; numbers in
// tabular-nums so they align vertically. Each stat is clickable in a future
// pass (e.g. "Open tasks: 7" → Workplan lens filtered to that account).
function HeaderStats({ data }: { data: AccountAggregate }) {
  const openDeals = data.deals.filter((d) => !d.closedAt).length;
  const stats: { label: string; value: number; tone?: string }[] = [
    { label: 'Open deals',     value: openDeals },
    { label: 'Engagements',    value: data.engagements.length },
    { label: 'Contacts',       value: data.contactsCount },
    { label: 'Open tasks',     value: data.openTasksCount, tone: data.openTasksCount > 0 ? '#7eb6e8' : undefined },
    { label: 'BD asks open',   value: data.bd.asksOpen,    tone: data.bd.asksOpen > 0 ? '#f0c674' : undefined },
    { label: 'To research',    value: data.bd.findingsToResearch, tone: data.bd.findingsToResearch > 0 ? '#f0a96b' : undefined },
  ];

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(3, auto)',
      gap: '8px 22px',
      padding: '10px 14px',
      border: '1px solid #232a37',
      borderRadius: 8,
      background: '#171c26',
      alignSelf: 'start',
    }}>
      {stats.map((s) => <Stat key={s.label} label={s.label} value={s.value} tone={s.tone} />)}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <span style={{
        fontSize: 22,
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
        color: tone ?? (value === 0 ? '#6b7a90' : '#e6e8ee'),
        lineHeight: 1,
      }}>
        {value}
      </span>
      <span style={{
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: 1,
        color: '#6b7a90',
        marginTop: 2,
      }}>
        {label}
      </span>
    </div>
  );
}

